<template>
  <ConfiguratorTextureCanvas
    ref="canvas"
    :background-color="backgroundColor"
    :glass-diameter="activeSize && activeSize.diameter"
    :glass-height="activeSize && activeSize.height"
    :width="activePrintingArea && activePrintingArea.width"
    :height="activePrintingArea && activePrintingArea.height"
    :printing-area-offset-top="
      activePrintingArea && activePrintingArea.offsetTop
    "
    :texture-src="
      (!isCustomTheme && activeConfiguration && activeConfiguration.url) ||
      undefined
    "
    class="configurator-texture-canvas-enhancer"
    v-bind="{
      fullTexture,
      includeBackground,
      layers,
      headless,
      resolution,
      color:
        (isActiveScreenPrintingColor &&
          activeScreenPrintingColor &&
          activeScreenPrintingColor.value) ||
        undefined,
    }"
  />
</template>

<script>
import ConfiguratorTextureCanvas from './ConfiguratorTextureCanvas';
import {
  ConfiguratorGetters,
  mapGetters as mapConfiguratorGetters,
} from '../../store/modules/configurator';
import {
  GlassesGetter,
  prefixType as prefixGlassesType,
} from '../../store/modules/configurator/glasses';
import ConfiguratorTextureCanvasMixin from '../../mixins/ConfiguratorTextureCanvas';
import { CustomModeMap } from '../../api/static/products';
import { ZoneType } from '../../api/static/zones';
import { filterProps } from '../../utils/ObjectUtils';
import { fitArea } from '../../utils/StageUtils';
import { loadImageAndCache } from '../../utils/ImageUtils';

export default {
  name: 'ConfiguratorTextureCanvasEnhancer',
  components: { ConfiguratorTextureCanvas },
  mixins: [ConfiguratorTextureCanvasMixin],
  props: {
    withPlaceholders: Boolean,
  },
  data() {
    return {
      layers: [],
    };
  },
  computed: {
    ...mapConfiguratorGetters({
      activeSize: ConfiguratorGetters.GET_ACTIVE_SIZE,
      activeColor: ConfiguratorGetters.GET_ACTIVE_COLOR,
      activePrintingArea: ConfiguratorGetters.GET_ACTIVE_PRINTING_AREA,
      activeConfiguration: ConfiguratorGetters.GET_ACTIVE_CONFIGURATION,
      activeProduct: ConfiguratorGetters.GET_ACTIVE_PRODUCT,
      isActiveScreenPrintingColor:
        ConfiguratorGetters.IS_ACTIVE_PRODUCT_VARIANT_SCREEN_PRINTING,
      activeScreenPrintingColor:
        ConfiguratorGetters.GET_ACTIVE_SCREEN_PRINTING_COLOR,
      customizationsGroupedByGroup:
        ConfiguratorGetters.GET_ZONE_CUSTOMIZATIONS_GROUPED_BY_GROUP,
    }),
    isCustomTheme() {
      return (this.activeProduct && this.activeProduct.isCustomizable) || false;
    },
    backgroundColor() {
      const colorValue = this.activeColor && this.activeColor.value;

      // For flat texture exports (no background needed), keep null to avoid drawing a background
      if (!this.includeBackground && !this.fullTexture) {
        return colorValue || undefined;
      }

      // For 3D renders, always guarantee a background color — fallback to translucent (#ddd)
      return colorValue || '#ddd';
    },
  },
  mounted() {
    this.$watch(
      function () {
        // Call properties that need to trigger a new render when updated
        void this.activeSize;
        void this.activeColor;
        void this.activePrintingArea;
        void this.activeConfiguration;
        void this.layers;
        void this.activeScreenPrintingColor;
        void this.resolution;

        return Date.now();
      },
      function () {
        // Wait until texture is ready to emit event
        this.$refs.canvas.renderReady().then(() => {
          this.$emit('change');
        });
      },
      { immediate: true }
    );

    this.$watch(
      function () {
        // Call properties that need to trigger a new render when updated
        void this.customizationsGroupedByGroup;
        void this.activeProduct;
        void this.activePrintingArea;
        void this.withPlaceholders;

        return Date.now();
      },
      async function () {
        await this.updateLayers();
      },
      { immediate: true }
    );
  },
  methods: {
    // Expose canvas export API to parent component
    ...['toBase64', 'toBlob'].reduce((acc, method) => {
      acc[method] = function () {
        return this.$refs.canvas[method]();
      };

      return acc;
    }, {}),
    async updateLayers() {
      const customizationsGroupedByGroup = this.customizationsGroupedByGroup;

      const layers = [];

      const findTypoById =
        this.$store.getters[prefixGlassesType(GlassesGetter.FIND_TYPO_BY_ID)];

      // Handle placeholder image per zone for custom products
      if (this.activeProduct.isCustomizable) {
        const customMode = CustomModeMap[this.activeProduct.id];
        const placeholderList =
          this.activePrintingArea.customProductPlaceholderImage[customMode] ??
          [];

        // Using for-loop because of async/await
        for (
          let index = 0, len = customizationsGroupedByGroup.length;
          index < len;
          index++
        ) {
          const { customizations: customizationList, zoneMap } =
            customizationsGroupedByGroup[index];

          // Create a fake image layer with placeholder image when zone group is pristine
          if (
            customizationList.some(
              customization =>
                customization.text ||
                customization.url ||
                zoneMap[customization.templateZoneId].defaultValue
            )
          ) {
            layers.push(
              ...customizationList.map(customization => {
                const zone = zoneMap[customization.templateZoneId];

                return {
                  ...zone,
                  _customization: customization,
                  typo: (zone.typoId && findTypoById(zone.typoId)) || null,
                };
              })
            );
          }
          // Create fake image layer
          else if (
            customizationList.length &&
            this.withPlaceholders &&
            placeholderList[index]
          ) {
            const image = await loadImageAndCache(placeholderList[index]);
            const templateZone = zoneMap[customizationList[0].templateZoneId]; // Using the first customization's width/height to create the fake layer

            const { width, height } = fitArea(image, templateZone);

            const fakeLayer = {
              id: -1 - index, // Using negative ID to avoid interferences with real zones
              type: ZoneType.IMAGE,
              // Assuming the first zone of the group determines the fake layer zone
              ...filterProps(templateZone, [
                'x',
                'y',
                'width',
                'height',
                'scale',
                'rotation',
              ]),
              _customization: {
                type: ZoneType.IMAGE,
                url: placeholderList[index],
                width,
                height,
                x: (templateZone.width - width) / 2,
                y: (templateZone.height - height) / 2,
                scaleX: 1,
                scaleY: 1,
              },
            };

            layers.push(fakeLayer);
          }
        }
      } else {
        layers.push(
          ...customizationsGroupedByGroup.reduce((acc, group) => {
            acc.push(
              ...group.customizations.map(customization => {
                const zone = group.zoneMap[customization.templateZoneId];

                // Convert data to the old zone/customization data model to avoid refactoring in ConfiguratorTextureCanvas
                return {
                  ...zone,
                  _customization: customization,
                  typo: (zone.typoId && findTypoById(zone.typoId)) || null,
                };
              })
            );

            return acc;
          }, [])
        );
      }

      this.layers = layers;
    },
  },
};
</script>
